import { backofficeAuthSignOut } from '@repo/sdk/gen'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/avatar'
import { Button } from '@repo/ui/button'
import { LanguagePicker } from '@repo/ui/components/language-picker'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@repo/ui/drawer'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar
} from '@repo/ui/sidebar'
import { getInitial } from '@repo/ui/utils'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut, UserRound } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { meQueryOptions } from '@/query/query-options'
import { clearSupportSearch } from '@/stores/use-support-search'

function AdminNavUser() {
	const { state, isMobile, setOpenMobile } = useSidebar()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { data: user } = useSuspenseQuery(meQueryOptions())

	const handleLogout = async () => {
		await backofficeAuthSignOut({ body: {} })
		// Clear before navigating: the (auth) gate reads the session cache-first,
		// so a stale authenticated entry would bounce the logout back to the app.
		queryClient.clear()
		clearSupportSearch()
		await navigate({ to: '/login' })
	}

	const closeMobileSidebar = () => {
		if (isMobile) {
			setOpenMobile(false)
		}
	}

	const initial = getInitial(user.name || user.email || '')
	const isCollapsed = state === 'collapsed' && !isMobile

	const userAvatar = (
		<Avatar className='size-8 rounded-lg' size='sm'>
			{user.image?.url ? (
				<AvatarImage
					alt={user.name ?? undefined}
					src={user.image.url}
				/>
			) : null}
			<AvatarFallback>{initial}</AvatarFallback>
		</Avatar>
	)

	if (isMobile) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<Drawer>
						<DrawerTrigger asChild>
							<SidebarMenuButton
								aria-label={t(
									'common:app_sidebar.user_menu.open'
								)}
								size='lg'
							>
								{userAvatar}
								<div className='grid min-w-0 flex-1 leading-tight'>
									<span
										className='truncate font-medium'
										title={user.name ?? undefined}
									>
										{user.name}
									</span>
									<span
										className='truncate text-sidebar-foreground/70 text-xs'
										title={user.email ?? undefined}
									>
										{user.email}
									</span>
								</div>
								<ChevronsUpDown className='ml-auto size-4 shrink-0 text-sidebar-foreground/70' />
							</SidebarMenuButton>
						</DrawerTrigger>
						<DrawerContent>
							<DrawerHeader className='border-b'>
								<div className='flex items-center gap-3 text-left'>
									{userAvatar}
									<div className='grid min-w-0 flex-1 leading-tight'>
										<DrawerTitle className='truncate text-sm'>
											{user.name}
										</DrawerTitle>
										<DrawerDescription className='truncate text-xs'>
											{user.email}
										</DrawerDescription>
									</div>
								</div>
							</DrawerHeader>
							<div className='flex flex-col gap-1 p-2'>
								<DrawerClose asChild>
									<Link
										className='flex h-11 items-center gap-3 rounded-md px-3 font-normal text-sm hover:bg-muted'
										onClick={closeMobileSidebar}
										preload={false}
										to='/profile'
									>
										<UserRound className='size-4' />
										<span>
											{t(
												'common:app_sidebar.menu.profile'
											)}
										</span>
									</Link>
								</DrawerClose>
								<LanguagePicker />
								<DrawerClose asChild>
									<Button
										className='h-11 justify-start gap-3 px-3 font-normal'
										onClick={handleLogout}
										variant='ghost'
									>
										<LogOut />
										<span>
											{t(
												'common:app_sidebar.menu.logout'
											)}
										</span>
									</Button>
								</DrawerClose>
							</div>
						</DrawerContent>
					</Drawer>
				</SidebarMenuItem>
			</SidebarMenu>
		)
	}

	const dropdownContent = (
		<DropdownMenuContent
			align='center'
			className='w-56'
			side={isCollapsed ? 'right' : 'top'}
			sideOffset={8}
		>
			<div className='flex items-center gap-2 p-2'>
				{userAvatar}
				<div className='grid min-w-0 flex-1 text-left text-sm leading-tight'>
					<span className='truncate font-medium text-foreground'>
						{user.name}
					</span>
					<span className='truncate text-muted-foreground text-xs'>
						{user.email}
					</span>
				</div>
			</div>
			<DropdownMenuSeparator />
			<DropdownMenuItem
				render={
					<Link preload={false} to='/profile'>
						<UserRound />
						<span>{t('common:app_sidebar.menu.profile')}</span>
					</Link>
				}
			/>
			<DropdownMenuSeparator />
			<LanguagePicker />
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={handleLogout}>
				<LogOut />
				<span>{t('common:app_sidebar.menu.logout')}</span>
			</DropdownMenuItem>
		</DropdownMenuContent>
	)

	if (isCollapsed) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger
							render={
								<Button
									aria-label={t(
										'common:app_sidebar.user_menu.open'
									)}
									className='size-8 rounded-lg border border-sidebar-border p-0 hover:bg-sidebar-accent aria-expanded:bg-sidebar-accent'
									size='icon-sm'
									variant='ghost'
								>
									{userAvatar}
								</Button>
							}
						/>
						{dropdownContent}
					</DropdownMenu>
				</SidebarMenuItem>
			</SidebarMenu>
		)
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger
						render={
							<SidebarMenuButton
								aria-label={t(
									'common:app_sidebar.user_menu.open'
								)}
								className='border border-sidebar-border'
								size='lg'
							>
								{userAvatar}
								<div className='grid min-w-0 flex-1 leading-tight'>
									<span
										className='truncate font-medium'
										title={user.name ?? undefined}
									>
										{user.name}
									</span>
									<span
										className='truncate text-sidebar-foreground/70 text-xs'
										title={user.email ?? undefined}
									>
										{user.email}
									</span>
								</div>
								<ChevronsUpDown className='ml-auto size-4 shrink-0 text-sidebar-foreground/70' />
							</SidebarMenuButton>
						}
					/>
					{dropdownContent}
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}

export { AdminNavUser }
